/**
 * Modal with contact form handling
 *
 * The script handles opening and closing the modal with contact form,
 * as well as resetting the form on each open and close.
 */
class ModalHandler {
	constructor() {
		// Check support for native dialog
		this.isDialogSupported = typeof HTMLDialogElement === 'function';

		// Get DOM elements
		this.contactDialog = document.getElementById('contact-dialog');
		this.toggleButtons = document.querySelectorAll('.modal-form-toggle');
		this.closeButton = this.contactDialog?.querySelector('.modal-close');
		this.form = this.contactDialog?.querySelector('form.wpcf7-form');

		// If there's no dialog on the page, end the initialization
		if (!this.contactDialog) return;

		this.initializeEventListeners();
	}

	/**
	 * Initialize all event listeners
	 */
	initializeEventListeners() {
		// Add event listeners to opening buttons
		this.toggleButtons.forEach(button => {
			button.addEventListener('click', (e) => {
				e.preventDefault();
				this.openModal();
			});
		});

		// Add event listener to close button
		if (this.closeButton) {
			this.closeButton.addEventListener('click', (e) => {
				e.preventDefault();
				this.closeModal();
			});
		}

		// Close dialog when clicking on backdrop
		if (this.isDialogSupported) {
			// For native dialog
			this.contactDialog.addEventListener('click', this.handleBackdropClick.bind(this));

			// ESC key handling for modal closing (native dialog behavior)
			this.contactDialog.addEventListener('cancel', () => {
				this.resetForm();
			});
		} else {
			// For polyfill - closing when clicking outside the dialog
			document.addEventListener('click', this.handleOutsideClick.bind(this));

			// ESC key handling for polyfill
			document.addEventListener('keydown', this.handleEscKey.bind(this));
		}
	}

	/**
	 * Opens modal with form
	 */
	openModal() {
		// Reset the form
		this.resetForm();

		// Open dialog in modal mode, or use polyfill for older browsers
		if (this.isDialogSupported) {
			this.contactDialog.showModal();
		} else {
			this.contactDialog.setAttribute('open', '');
		}
		document.body.classList.add('dialog-open');

		// Focus on the first form field (optional)
		setTimeout(() => {
			const firstInput = this.form?.querySelector('input:not([type="hidden"]):not([type="submit"])');
			if (firstInput) firstInput.focus();
		}, 100);
	}

	/**
	 * Closes the modal
	 */
	closeModal() {
		// Reset the form
		this.resetForm();

		// Close dialog or use polyfill
		if (this.isDialogSupported) {
			this.contactDialog.close();
		} else {
			this.contactDialog.removeAttribute('open');
		}
		document.body.classList.remove('dialog-open');
	}

	/**
	 * Resets the contact form
	 */
	resetForm() {
		if (this.form) {
			this.form.reset();

			// Remove all error and success messages
			const responseWrapper = this.form.querySelector('.wpcf7-response-output');
			if (responseWrapper) responseWrapper.innerHTML = '';

			// Remove validation classes from fields
			const invalidFields = this.form.querySelectorAll('.wpcf7-not-valid');
			invalidFields.forEach(field => {
				field.classList.remove('wpcf7-not-valid');
			});

			// Remove error messages next to fields
			const errorMessages = this.form.querySelectorAll('.wpcf7-not-valid-tip');
			errorMessages.forEach(message => {
				message.remove();
			});
		}
	}

	/**
	 * Handles clicks on the modal backdrop
	 */
	handleBackdropClick(e) {
		const rect = this.contactDialog.getBoundingClientRect();
		const isInDialog =
			rect.top <= e.clientY &&
			e.clientY <= rect.top + rect.height &&
			rect.left <= e.clientX &&
			e.clientX <= rect.left + rect.width;

		if (!isInDialog) {
			this.closeModal();
		}
	}

	/**
	 * Handles clicks outside the modal for polyfill
	 */
	handleOutsideClick(e) {
		if (this.contactDialog.hasAttribute('open') &&
			!this.contactDialog.contains(e.target) &&
			!Array.from(this.toggleButtons).includes(e.target)) {
			this.closeModal();
		}
	}

	/**
	 * Handles ESC key press for polyfill
	 */
	handleEscKey(e) {
		if (e.key === 'Escape' && this.contactDialog.hasAttribute('open')) {
			this.closeModal();
		}
	}
}

// Initialize modal handler when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
	new ModalHandler();
});
